import * as React from 'react';
import styled from '@emotion/styled';
import propTypes from '@styled-system/prop-types';
import { bool } from 'prop-types';
import { compose, color, typography, get } from 'styled-system';
import shouldForwardProp from '@styled-system/should-forward-prop';
import { createNamedContext } from '../../../utils';
import { basekickLineheightScale } from '../../../utils/styled';

const TextContext = createNamedContext('Text');

function useTextContext() {
  return React.useContext(TextContext);
}

const StyledText = styled('div', { shouldForwardProp })((props) => {
  if (!props.trim) return;
  let lineHeight = get(props.theme.lineHeights, props.lineHeight, props.lineHeight);
  if (typeof lineHeight !== 'number') {
    throw new Error(
      `Text: Only unitless numbers allowed for \`lineHeight\` prop, but you provided \`${lineHeight}\``,
    );
  }
  return basekickLineheightScale(lineHeight, props);
}, compose(color, typography));

const Text = (props) => {
  const isInText = useTextContext();
  const {
    children,
    as = isInText ? 'span' : 'div',
    color = isInText ? 'inherit' : 'foreground',
    fontSize = isInText ? 'inherit' : 'standard',
    fontWeight = isInText ? 'inherit' : 'standard',
    fontFamily = isInText ? 'inherit' : 'standard',
    lineHeight = isInText ? 'inherit' : 'standard',
    trim = !isInText,
    ...rest
  } = props;

  return (
    <TextContext.Provider value>
      <StyledText
        as={as}
        color={color}
        fontSize={fontSize}
        fontWeight={fontWeight}
        fontFamily={fontFamily}
        lineHeight={lineHeight}
        trim={trim}
        {...rest}
      >
        {children}
      </StyledText>
    </TextContext.Provider>
  );
};

Text.propTypes = {
  ...propTypes.color,
  ...propTypes.typography,
  /**
   * Whether to trim whitespace from the top and bottom of the text
   */
  trim: bool,
};

export default Text;
export { useTextContext };
